import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useStyles } from './CustomizedMultipleInput.styles';
import CircularProgress from '@mui/material/CircularProgress';
import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FormLabel, IconButton } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { isObject } from 'lodash';
import {
    BRAND_MODAL_ACCENT_ICON,
    BRAND_MODAL_ACCENT_LIST_SELECTED,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_OUTLINE_VARIANT,
    brandModalOutlinedInputSx,
} from '../../pages/BrandDashboard/brandDashboardTokens';

export interface ICustomizedMultipleInput {
    style?: any;
    textInputStyle?: any;
    onFocus?: (e: any) => void;
    onBlur?: (e: any) => void;
    loading?: boolean;
    enableChevron?: boolean;
    maximumSelectable?: number;
    disabled?: boolean;
    autoCompleteOptions?: string[];
    entyLimit?: number;
    onChange?: (args: any) => void;
    initialValue?: any[];
    disableInput?: boolean;
    single?: boolean;
    customizedLabel?: string;
    initialOption?: string[];
    freeSolo?: boolean;
    textInputProps?: TextFieldProps;
    multiple?: boolean;
    placeholder?: string;
    /** Brand system forms — obsidian autocomplete, gold focus */
    brandForm?: boolean;
    /** Should determine if we load the options dynamically based on a selection */
    enableDynamicList?: boolean;
    /**  Which field should this depend on*/
    dependencyField?: string;
    /** Lst of the values that it should have */
    dynamicList?: {
        [key: string]: string[];
    };
}

export const CustomizedMultipleInput: React.FC<ICustomizedMultipleInput> = ({
    style,
    textInputStyle,
    onFocus,
    onBlur,
    loading,
    enableChevron,
    maximumSelectable = 2,
    disabled,
    autoCompleteOptions,
    entyLimit = 3,
    onChange,
    initialValue,
    disableInput,
    single,
    customizedLabel,
    initialOption,
    freeSolo,
    textInputProps,
    multiple,
    placeholder,
    brandForm = false,
}) => {
    const [inputValue, setInputValue] = React.useState('');
    const [, setIsFocused] = useState(false);
    const [value, setValue] = React.useState<any>([]);
    const shouldShowNoOptionsText = inputValue.length > entyLimit;
    const [open, setOpen] = useState(false);
    const { themeStyle } = useThemeContext();
    const classes = useStyles(shouldShowNoOptionsText, themeStyle, brandForm)();
    const [optionsToSelect, setOptionsToSelect] = useState<string[]>([]);
    const inputRef = React.useRef<any>(null);
    const showChevron = enableChevron ?? brandForm;

    const handleInputChange = (event: any) => {
        if (event && event.target && event.target.value && !disableInput) {
            setInputValue(event.target.value);
            setOpen(true);
        }
    };

    React.useEffect(() => {
        if (initialValue) {
            setValue(initialValue);
        }
    }, [initialValue]);

    React.useEffect(() => {
        if (initialOption) {
            setOptionsToSelect(initialOption);
        }
    }, [initialOption]);

    const handleInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && inputValue.length > entyLimit) {
            event.preventDefault(); // <-- prevent page refresh
            event.stopPropagation();
            const newEntry = inputValue;
            setOptionsToSelect(prevValue => [...prevValue, newEntry]);
            if (single) {
                setValue([value]);
            } else {
                setValue([...value, newEntry]);
            }
            if (onChange) {
                onChange([...value, newEntry]);
            }
            setInputValue('');
            setOpen(false);
        }
        if (event.key === 'Backspace' && inputValue.length <= 1) {
            setInputValue('');
        }
    };

    const handleOnFocus = (event?: any) => {
        setIsFocused(true);
        onFocus && onFocus(event);
        if (disableInput || brandForm) {
            setOpen(true);
        }
    };

    const handleOnBlur = (event?: any) => {
        setIsFocused(false);
        onBlur && onBlur(event);
        setOpen(false);
        setInputValue('');
    };

    React.useEffect(() => {
        if (autoCompleteOptions) {
            setOptionsToSelect(autoCompleteOptions);
        }
    }, [autoCompleteOptions]);

    return (
        <>
            {!!customizedLabel && (
                <FormLabel
                    style={{
                        maxWidth: 400,
                        width: '100%',
                        textAlign: 'left',
                        fontSize: 14,
                        fontStyle: 'normal',
                        fontWeight: '400',
                        color: theme[themeStyle].form_label,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        lineHeight: 2.5,
                    }}>
                    {customizedLabel}
                </FormLabel>
            )}
            <Autocomplete
                classes={classes}
                multiple={maximumSelectable > 1 || multiple}
                limitTags={maximumSelectable}
                id="multiple-limit-tags"
                options={[...new Set(optionsToSelect)]}
                style={style}
                open={open}
                getOptionLabel={option => {
                    return isObject(option) ? '' : option;
                }}
                freeSolo={freeSolo}
                onKeyDown={e => {
                    if ((e as React.KeyboardEvent).key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
                ref={inputRef}
                disabled={disabled}
                draggable
                disableClearable
                loading={loading}
                blurOnSelect
                filterSelectedOptions
                ChipProps={{
                    color: 'primary',
                    sx: brandForm
                        ? {
                              '& .MuiChip-label': {
                                  textTransform: 'capitalize',
                              },
                          }
                        : undefined,
                }}
                value={value}
                onFocus={handleOnFocus}
                autoSelect
                onBlur={handleOnBlur}
                isOptionEqualToValue={(option, value) => {
                    return !!value;
                }}
                onChange={(event, newValue) => {
                    setValue(newValue);
                    setInputValue('');
                    if (onChange) {
                        onChange(newValue);
                    }
                }}
                ListboxProps={{
                    sx: {
                        backgroundColor: brandForm ? BRAND_SURFACE_CONTAINER_HIGHEST : theme[themeStyle].form_gray,
                        color: theme[themeStyle].lightWhite,
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        border: brandForm ? `1px solid ${BRAND_OUTLINE_VARIANT}` : undefined,
                        '& .MuiAutocomplete-option': {
                            '&.Mui-focused': {
                                backgroundColor: brandForm
                                    ? 'rgba(255,255,255,0.06) !important'
                                    : undefined,
                            },
                            '&[aria-selected="true"]': {
                                backgroundColor: brandForm
                                    ? `${BRAND_MODAL_ACCENT_LIST_SELECTED} !important`
                                    : undefined,
                            },
                        },
                    },
                }}
                componentsProps={
                    brandForm
                        ? {
                              paper: {
                                  sx: {
                                      bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                      border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                      backgroundImage: 'none',
                                  },
                              },
                          }
                        : undefined
                }
                inputValue={inputValue}
                onInputChange={handleInputChange}
                noOptionsText={shouldShowNoOptionsText ? 'Press enter to save' : ''}
                renderInput={params => (
                    <TextField
                        {...textInputProps}
                        {...params}
                        onKeyDown={handleInputKeyPress}
                        style={textInputStyle}
                        variant={brandForm ? 'outlined' : 'filled'}
                        onFocus={handleOnFocus}
                        placeholder={placeholder}
                        onBlur={handleOnBlur}
                        sx={brandForm ? { width: '100%', ...brandModalOutlinedInputSx, ...(textInputProps?.sx as object) } : textInputProps?.sx}
                        InputLabelProps={{
                            style: {
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 8,
                            },
                        }}
                        InputProps={{
                            ...params.InputProps,
                            disableUnderline: true, // Remove the underline in the textfield
                            placeholder: placeholder,
                            classes: {
                                root: classes.inputRoot,
                            },
                            endAdornment: (
                                <React.Fragment>
                                    {loading ? (
                                        <CircularProgress
                                            size={20}
                                            sx={{ color: brandForm ? BRAND_MODAL_ACCENT_ICON : undefined }}
                                        />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                    {showChevron ? (
                                        <IconButton
                                            type="button"
                                            tabIndex={-1}
                                            size="small"
                                            disableRipple
                                            aria-label="Open options"
                                            onMouseDown={e => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setOpen(o => !o);
                                            }}
                                            sx={{
                                                p: 0.25,
                                                mr: brandForm ? 0.25 : 0.5,
                                                color: brandForm ? BRAND_MODAL_ACCENT_ICON : undefined,
                                            }}>
                                            <KeyboardArrowDownIcon sx={{ opacity: brandForm ? 0.92 : 1 }} />
                                        </IconButton>
                                    ) : null}
                                </React.Fragment>
                            ),
                        }}
                    />
                )}
                sx={{
                    width: '100%',
                }}
            />
        </>
    );
};
